/*
 * The web app's address is https://engrams.pages.dev (owner, 2026-10-11: make visits to the old address jump there).
 * The workers.dev address stays for the Android app's API, share links and tools, and its Worker sends a fresh page
 * load straight on (apps/server/src/index.ts). A browser that already runs Engram there never asks the Worker (its
 * service worker answers page loads), so the app moves itself, but only with nothing left to sync: each address keeps
 * its own local copy, and an unsent change would stay behind at the old one. It moves on its own while the tab is
 * hidden, or just opened and untouched; otherwise a banner offers to go. Moving unregisters this address's service
 * worker, so later visits get the Worker's redirect straight away.
 */
import { create } from "zustand";
import { isNative } from "./api";
import { db, getSyncState, onSyncState } from "./db/client";

export const WEB_HOME = "https://engrams.pages.dev";
export const onOldAddress = !isNative && location.hostname.endsWith(".workers.dev");

export const useMove = create<{ offer: boolean; moving: boolean; error: string | null }>(() => ({ offer: false, moving: false, error: null }));

const loadedAt = Date.now();
let touched = false;
let watching = false;

async function go() {
  if (useMove.getState().moving) return;
  useMove.setState({ moving: true });
  try {
    const regs = (await navigator.serviceWorker?.getRegistrations()) ?? [];
    await Promise.all(regs.map((r) => r.unregister()));
    const keys = await caches.keys();
    await Promise.all(keys.filter((k) => k.startsWith("engram-app-")).map((k) => caches.delete(k)));
  } catch {
    /* it still moves; the next visit here loads the app once more and moves again */
  }
  location.replace(WEB_HOME + location.pathname + location.search + location.hash);
}

/** Saves what's being typed and sends what waits; true when this copy has nothing left that the server lacks. */
async function synced(): Promise<boolean> {
  await db.saveAll(1500);
  let s = getSyncState();
  if (s.pending > 0 || s.status !== "idle") s = await db.sync().catch(() => getSyncState());
  return s.pending === 0 && s.status === "idle";
}

/** Called when the app shows the sign-in screen or the app itself. */
export function startMove(phase: "login" | "app") {
  if (!onOldAddress || useMove.getState().moving) return;
  if (phase === "login") {
    // Not signed in here: go, unless an earlier session left changes that never reached the server.
    void db
      .query<{ n: number }>("SELECT COUNT(*) AS n FROM outbox")
      .then((r) => {
        if (r[0]?.n === 0) void go();
      })
      .catch(() => undefined);
    return;
  }
  if (watching) return;
  watching = true;
  useMove.setState({ offer: true });
  const mark = () => (touched = true);
  addEventListener("pointerdown", mark, { capture: true, once: true });
  addEventListener("keydown", mark, { capture: true, once: true });
  const early = () => !touched && Date.now() - loadedAt < 10_000;
  const auto = async () => {
    if (document.visibilityState !== "hidden" && !early()) return;
    if (!(await synced())) return;
    if (document.visibilityState === "hidden" || early()) void go();
  };
  onSyncState((s) => {
    if (s.status === "idle" && s.pending === 0) void auto();
  });
  document.addEventListener("visibilitychange", () => void auto());
}

/** The banner's "Go there". */
export async function moveNow() {
  useMove.setState({ error: null });
  if (await synced()) return go();
  const s = getSyncState();
  useMove.setState({
    error: s.pending
      ? `${s.pending} change(s) here haven't reached the server yet. Try again once they have.`
      : s.status === "error"
        ? `Sync error${s.error ? `: ${s.error}` : ""}. Try again once it's fixed.`
        : "Can't reach the server right now. Try again in a moment.",
  });
}
